import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { listOptions, useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface RefOption {
  id: number;
  name: string;
}

interface JobOption {
  id: number;
  job_title: string;
}

interface OrgUnitOption {
  id: number;
  unit_name: string;
}

interface LocationOption {
  id: number;
  location_name: string;
}

interface PositionRow {
  id: number;
  position_code: string;
  position_title: string;
  job_id: number;
  org_unit_id: number;
  location_id: number | null;
  reports_to_position_id: number | null;
  budgeted_fte: number;
  pay_grade_id: number;
  work_shift_id: number;
  status: string;
  job: JobOption;
  org_unit: OrgUnitOption;
  location: LocationOption | null;
  reports_to: PositionRow | null;
  pay_grade: RefOption;
  work_shift: RefOption;
}

interface PositionForm {
  position_title: string;
  job_id: number | '';
  org_unit_id: number | '';
  location_id: number | '';
  reports_to_position_id: number | '';
  budgeted_fte: number | string;
  pay_grade_id: number | '';
  work_shift_id: number | '';
  status: string;
}

const emptyForm: PositionForm = {
  position_title: '',
  job_id: '',
  org_unit_id: '',
  location_id: '',
  reports_to_position_id: '',
  budgeted_fte: 1,
  pay_grade_id: '',
  work_shift_id: '',
  status: '',
};

export function Positions() {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PositionRow[] | null>(null);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitOption[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [payGrades, setPayGrades] = useState<RefOption[]>([]);
  const [workShifts, setWorkShifts] = useState<RefOption[]>([]);
  const [editing, setEditing] = useState<'new' | PositionRow | null>(null);
  const [form, setForm] = useState<PositionForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');

  const load = (searchTerm?: string) =>
    api.get<PositionRow[]>('/positions', { params: { search: searchTerm || undefined } }).then((res) => setItems(res.data));

  useEffect(() => {
    api.get<JobOption[]>('/jobs').then((res) => setJobs(res.data));
    api.get<OrgUnitOption[]>('/org-units').then((res) => setOrgUnits(res.data));
    api.get<LocationOption[]>('/locations').then((res) => setLocations(res.data));
    api.get<RefOption[]>('/pay-grades').then((res) => setPayGrades(res.data));
    api.get<RefOption[]>('/work-shifts').then((res) => setWorkShifts(res.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => load(search), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const statuses = listOptions(lists, 'position_status');

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PositionRow) => {
    setForm({
      position_title: item.position_title,
      job_id: item.job_id,
      org_unit_id: item.org_unit_id,
      location_id: item.location_id ?? '',
      reports_to_position_id: item.reports_to_position_id ?? '',
      budgeted_fte: item.budgeted_fte,
      pay_grade_id: item.pay_grade_id,
      work_shift_id: item.work_shift_id,
      status: item.status,
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof PositionForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, location_id: form.location_id || null, reports_to_position_id: form.reports_to_position_id || null };
      if (editing === 'new') await api.post('/positions', payload);
      else if (editing) await api.put(`/positions/${editing.id}`, payload);
      setEditing(null);
      load(search);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PositionRow) => {
    if (!(await confirmDelete(`Delete "${item.position_title}"?`))) return;
    try {
      await api.delete(`/positions/${item.id}`);
      load(search);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const labelOf = (options: { value: string; label: string }[], value: string) =>
    options.find((o) => o.value === value)?.label ?? value;

  return (
    <>
      <header className="page-header">
        <h1>Positions</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by title or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Position Title</th>
                <th>Org Unit</th>
                <th>Reports To</th>
                <th>Pay Grade</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.position_code}</td>
                  <td>
                    <strong>{item.position_title}</strong>
                  </td>
                  <td>{item.org_unit?.unit_name}</td>
                  <td className="muted">{item.reports_to?.position_title ?? '—'}</td>
                  <td className="muted">{item.pay_grade?.name}</td>
                  <td>
                    <span className="badge badge-neutral">{labelOf(statuses, item.status)}</span>
                  </td>
                  <td className="right">
                    <IconButton icon={IconView} label="View" to={`/hr/positions/${item.id}`} />
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No positions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add position' : 'Edit position'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Position Code
              <input value={editing === 'new' ? 'Auto-generated on save' : editing.position_code} disabled />
            </label>
            <label>
              Position Title *
              <input value={form.position_title} onChange={set('position_title')} required />
              {errors.position_title && <small className="error">{errors.position_title[0]}</small>}
            </label>
            <label>
              Job *
              <select value={form.job_id} onChange={set('job_id')} required>
                <option value="">—</option>
                {jobs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.job_title}
                  </option>
                ))}
              </select>
              {errors.job_id && <small className="error">{errors.job_id[0]}</small>}
            </label>
            <label>
              Org Unit *
              <select value={form.org_unit_id} onChange={set('org_unit_id')} required>
                <option value="">—</option>
                {orgUnits.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.unit_name}
                  </option>
                ))}
              </select>
              {errors.org_unit_id && <small className="error">{errors.org_unit_id[0]}</small>}
            </label>
            <label>
              Location
              <select value={form.location_id} onChange={set('location_id')}>
                <option value="">None</option>
                {locations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.location_name}
                  </option>
                ))}
              </select>
              {errors.location_id && <small className="error">{errors.location_id[0]}</small>}
            </label>
            <label>
              Reports To
              <select value={form.reports_to_position_id} onChange={set('reports_to_position_id')}>
                <option value="">None</option>
                {items
                  ?.filter((i) => editing === 'new' || i.id !== editing.id)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.position_title}
                    </option>
                  ))}
              </select>
              {errors.reports_to_position_id && <small className="error">{errors.reports_to_position_id[0]}</small>}
            </label>
            <label>
              Pay Grade *
              <select value={form.pay_grade_id} onChange={set('pay_grade_id')} required>
                <option value="">—</option>
                {payGrades.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              {errors.pay_grade_id && <small className="error">{errors.pay_grade_id[0]}</small>}
            </label>
            <label>
              Work Shift *
              <select value={form.work_shift_id} onChange={set('work_shift_id')} required>
                <option value="">—</option>
                {workShifts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              {errors.work_shift_id && <small className="error">{errors.work_shift_id[0]}</small>}
            </label>
            <label>
              Budgeted FTE *
              <input type="number" step="0.01" min="0" value={form.budgeted_fte} onChange={set('budgeted_fte')} required />
              {errors.budgeted_fte && <small className="error">{errors.budgeted_fte[0]}</small>}
            </label>
            <label className="span-2">
              Status *
              <select value={form.status} onChange={set('status')} required>
                <option value="">—</option>
                {statuses.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {errors.status && <small className="error">{errors.status[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
